/**
 * Selection UI interaction sink: turns coarse UI events (indices, phases) into
 * metrics and product analytics with stable ids and DOM context.
 */
import {
  analyticsClient,
  getDatabaseContainerType,
  getSelectionsExperimentAttributes,
} from '@databases/analytics';
import { createMetricRef, fireMetric } from '@databases/frontend-metrics/fire-metric';
import { cellInlineEditMetric } from '@databases/frontend-metrics/metrics/cell-inline-edit';
import { cellSelectionMetric } from '@databases/frontend-metrics/metrics/cell-selection';
import { entryCheckboxMetric } from '@databases/frontend-metrics/metrics/entry-checkbox';
import { headerCheckboxMetric } from '@databases/frontend-metrics/metrics/header-checkbox';
import { type SelectionUiInteraction } from '@databases/selections';
import { type DatabaseAdapter } from '@databases/selections-api-types';

import { getExperimentValue } from '@canvas/client-feature-flags';

const LAYOUT_TABLE_SELECTOR = '[data-testid="layout-table"]';

/**
 * Bootstrap-local handle: synchronous `emit`, clears cached DOM on `dispose`.
 */
export type SelectionUiInteractionSinkHandle = {
  emit(event: SelectionUiInteraction): void;
  dispose(): void;
};

/**
 * Factory for the databases selection UI metrics path. The table emits
 * {@link SelectionUiInteraction} with row/column indices; this sink resolves them to
 * `entryId` / `fieldId` via `databaseAdapter` (view-aware, current snapshot per call)
 * and records frontend metrics. Header checkbox selection also sends an analytics
 * event on a microtask so the hot path stays light.
 *
 * Metrics fire synchronously from `emit` except that header checkbox product
 * analytics is scheduled with `queueMicrotask`. If indices are out of range or
 * the layout table root is missing, metrics may no-op or fail fast per metric
 * configuration.
 *
 * Caches the layout table element (`[data-testid="layout-table"]`) after first
 * resolve; call `dispose` when tearing down the feature so the next mount can
 * re-query the DOM.
 */
export function createSelectionUiInteractionSink(params: {
  /**
   * Database content id attached to metric metadata and analytics payloads.
   */
  contentId: string;
  /**
   * Maps visible grid indices to ids for the current view; avoids copying full id lists per emit.
   */
  databaseAdapter: DatabaseAdapter;
}): SelectionUiInteractionSinkHandle {
  const { contentId, databaseAdapter } = params;

  let cachedLayoutTable: HTMLElement | null | undefined = undefined;

  const cellSelectionMetricRef = createMetricRef();
  const cellInlineEditMetricRef = createMetricRef();
  const entryCheckboxMetricRef = createMetricRef();
  const headerCheckboxMetricRef = createMetricRef();

  function getTableRoot(): HTMLElement | null {
    if (cachedLayoutTable !== undefined && cachedLayoutTable !== null) {
      return cachedLayoutTable;
    }

    const el = document.querySelector<HTMLElement>(LAYOUT_TABLE_SELECTOR);

    if (el) {
      cachedLayoutTable = el;
    }

    return el;
  }

  function emitCellPointer(
    event: Extract<SelectionUiInteraction, { source: 'cellPointer' }>
  ) {
    const cohort = getExperimentValue('databasesSelectionsPerformance', 'cohort', {
      fireExperimentExposure: false,
    }) as 'control' | 'test';

    const tableRoot = getTableRoot();
    const entryId = databaseAdapter.getVisibleEntryIdAt(event.detail.rowIndex);
    const fieldId = databaseAdapter.getVisibleFieldIdAt(event.detail.colIndex);

    if (!entryId || !fieldId) {
      return;
    }

    if (event.detail.phase === 'selectionCommitted') {
      fireMetric(
        cellSelectionMetric,
        {
          startTime: event.startTime,
          probeData: {
            tableRoot,
            selectionProbe: 'focusBox',
            entryId,
            fieldId,
          },
          metadata: {
            contentId,
            databasesSelectionsPerformanceCohort: cohort,
            selectionProbe: 'focusBox',
          },
          skipIfInFlight: true,
          ...(!tableRoot && {
            failImmediately: { reason: 'cell-selection-dom-sync-missing-table-root' },
          }),
        },
        cellSelectionMetricRef
      );

      return;
    }

    fireMetric(
      cellInlineEditMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          entryId,
          fieldId,
        },
        metadata: {
          contentId,
          databasesSelectionsPerformanceCohort: cohort,
        },
        skipIfInFlight: true,
        ...(!tableRoot && {
          failImmediately: { reason: 'cell-inline-edit-dom-sync-missing-table-root' },
        }),
      },
      cellInlineEditMetricRef
    );
  }

  function emitRowCheckbox(
    event: Extract<SelectionUiInteraction, { source: 'rowCheckbox' }>
  ) {
    const entryId = databaseAdapter.getVisibleEntryIdAt(event.detail.rowIndex);

    if (!entryId) {
      return;
    }

    const tableRoot = getTableRoot();

    fireMetric(
      entryCheckboxMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          probe: {
            kind: 'single-row',
            entryId,
            expectRowCheckboxChecked: event.detail.expectRowCheckboxChecked,
          },
        },
        metadata: {
          contentId,
          isShiftRangeSelect: event.modifiers.shift,
          databasesSelectionsPerformanceCohort: 'test',
        },
        skipIfInFlight: true,
        ...(!tableRoot && {
          failImmediately: { reason: 'entry-checkbox-dom-sync-missing-table-root' },
        }),
      },
      entryCheckboxMetricRef
    );

    queueMicrotask(() => {
      void analyticsClient
        .sendRowCheckboxSelectedEvent({
          entryId,
          contentId,
          databaseContainer: getDatabaseContainerType(),
          checkboxState: event.detail.expectRowCheckboxChecked
            ? 'selected'
            : 'unselected',
          ...getSelectionsExperimentAttributes(contentId),
        })
        .catch(() => undefined);
    });
  }

  function emitHeaderCheckbox(
    event: Extract<SelectionUiInteraction, { source: 'headerCheckbox' }>
  ) {
    const cohort = getExperimentValue('databasesSelectionsPerformance', 'cohort', {
      fireExperimentExposure: false,
    }) as 'control' | 'test';

    const tableRoot = getTableRoot();

    fireMetric(
      headerCheckboxMetric,
      {
        startTime: event.startTime,
        probeData: {
          tableRoot,
          expectChecked: event.detail.expectChecked,
        },
        metadata: {
          contentId,
          databasesSelectionsPerformanceCohort: cohort,
        },
        skipIfInFlight: true,
        ...(!tableRoot && {
          failImmediately: { reason: 'header-checkbox-dom-sync-missing-table-root' },
        }),
      },
      headerCheckboxMetricRef
    );

    queueMicrotask(() => {
      void analyticsClient
        .sendHeaderCheckboxSelectedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          entriesCount: databaseAdapter.getVisibleEntryCount(),
          checkboxState: event.detail.checkboxState,
          ...getSelectionsExperimentAttributes(contentId),
        })
        .catch(() => undefined);
    });
  }

  return {
    emit(event: SelectionUiInteraction) {
      if (event.source === 'cellPointer') {
        emitCellPointer(event);

        return;
      }

      if (event.source === 'rowCheckbox') {
        emitRowCheckbox(event);

        return;
      }

      emitHeaderCheckbox(event);
    },

    dispose() {
      cachedLayoutTable = undefined;
    },
  };
}
